/**
 * THE 1v1 DUEL — ball exposure, pressure, and whether a challenge is worth
 * making. (Phase-1 Commits 4-6)
 *
 * This module answers three questions the AI could not previously ask:
 *
 *   1. `calculatePressure`  — how pressed is this player, in TIME rather than
 *      in metres? Two defenders three metres away are not equal pressure if
 *      one is sprinting at you and the other has his back turned.
 *   2. `ballExposure`       — how loose is this carrier's ball right now? The
 *      single strongest natural defensive trigger, and the thing that turns a
 *      contain into a challenge.
 *   3. `scoreTackleOpportunity` — given that a challenge is physically legal,
 *      is it a good idea?
 *
 * ── WHY IT LIVES IN /sim AND NOT /ai ────────────────────────────────────────
 *
 * The same lesson `tackle-geometry.ts` records in its own header: the AI's
 * commit decision and the sim's referee must never disagree about the
 * geometry, and the way you guarantee that is one arithmetic in one file. The
 * duel is the same shape of problem — the AI decides to challenge on these
 * numbers, the debug overlay renders these numbers, and any future sim-side
 * consumer reads these numbers. A private copy in the AI is how they drift.
 *
 * ── WHAT THIS FILE IS NOT ───────────────────────────────────────────────────
 *
 * It is NOT a replacement for `reachesBallBeforeMan`. That predicate stays a
 * HARD GATE above everything here: physically impossible or plainly illegal
 * challenges are rejected outright, and only what survives gets scored. The
 * owner's instruction was explicit about that ordering, and it is the right
 * one — turning a safety constraint into one term of a fuzzy sum is how a bot
 * talks itself into a red card.
 */

import { MAX_PLAYERS, type WorldState } from './types.js';
import {
  BALL_EXPOSED_DIST_M,
  BALL_EXPOSED_REL_SPEED,
  DUEL_LAST_DEFENDER_PENALTY,
  DUEL_OPPORTUNITY_W_ADVANTAGE,
  DUEL_OPPORTUNITY_W_EXPOSURE,
  DUEL_OPPORTUNITY_W_SUPPORT,
  DUEL_AWARENESS_RADIUS_M,
  DUEL_SUPPORT_RADIUS_M,
  ENGAGE_LEAD_MAX_S,
  ENGAGE_STEP_RANGE_M,
  ENGAGE_TREND_FULL,
  ENGAGE_W_ADVANTAGE,
  ENGAGE_W_EXPOSURE,
  ENGAGE_W_SUPPORT,
  ENGAGE_W_TREND,
  DUEL_VISION_HALF_RAD,
  MAX_SPEED_RUN,
  POSSESSION_RADIUS,
  PRESSURE_HORIZON_S,
  PRESSURE_MAX_RANGE_M,
  TOUCH_RADIUS,
} from './constants.js';
import { ballFirstMargin } from './tackle-geometry.js';
import { angleDelta } from '../math/vec3.js';

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
const distXZ = (ax: number, az: number, bx: number, bz: number): number =>
  Math.sqrt((ax - bx) * (ax - bx) + (az - bz) * (az - bz));

/**
 * Can `id` perceive a point? (§10.2)
 *
 * ⚠ /sim MUST NOT IMPORT /ai, and `canSee` lives in ai/individual.ts, so this
 * is the same rule stated here rather than an import. It is deliberately the
 * SAME two gates — awareness radius, then vision half-cone — because "it must
 * not react to a player behind it" is a perception rule, not an AI policy, and
 * two different answers to it would be worse than one duplicated one.
 */
function perceives(state: WorldState, id: number, tx: number, tz: number): boolean {
  const p = state.players;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  if (distXZ(px, pz, tx, tz) > DUEL_AWARENESS_RADIUS_M) return false;
  const want = Math.atan2(tz - pz, tx - px);
  return Math.abs(angleDelta(p.yaw[id]!, want)) <= DUEL_VISION_HALF_RAD;
}

/** What `calculatePressure` returns. Reused as a scratch object per call site
 *  so the steady state allocates nothing (§15 — the sim allocates no garbage). */
export interface PressureInfo {
  /** 0..1. */
  value: number;
  /** The opponent generating most of it, or -1. */
  primaryOpponent: number;
  /** Unit vector FROM the pressure TOWARD the player — the escape direction. */
  directionX: number;
  directionZ: number;
  /** Seconds until the primary opponent reaches him. Infinity when none. */
  arrivalTime: number;
}

export function createPressureInfo(): PressureInfo {
  return { value: 0, primaryOpponent: -1, directionX: 0, directionZ: 0,
           arrivalTime: Infinity };
}

/**
 * How long before `from` reaches `x,z`, in seconds — a first-order arrival
 * time, not a full motion solve.
 *
 * It charges for the two things that make a nearby defender harmless: the turn
 * he has to make, and the speed he does not currently have in the right
 * direction. A man sprinting at you and a man with his back turned are both
 * "three metres away" and are not remotely the same threat, and this is the
 * cheapest honest way to say so.
 *
 * ⚠ NOT a replacement for `solveIntercept`, which solves a MOVING ball's
 * trajectory and stays the tool for that. This is the player half.
 */
export function estimatePlayerArrival(
  state: WorldState, from: number, x: number, z: number,
): number {
  const p = state.players;
  const dx = x - p.px[from]!;
  const dz = z - p.pz[from]!;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d < 1e-4) return 0;
  const vx = p.vx[from]!;
  const vz = p.vz[from]!;
  const speed = Math.sqrt(vx * vx + vz * vz);
  // Component of his current velocity actually pointed at the target. Negative
  // when he is running the other way, which is exactly when he should be
  // charged the most.
  const closing = speed < 1e-4 ? 0 : (vx * dx + vz * dz) / d;
  // The turn cost: full when he is going the wrong way, none when he is
  // already pointed at it.
  const align = speed < 1e-4 ? 0 : clamp01(closing / speed);
  const turnCost = (1 - align) * PRESSURE_HORIZON_S * 0.5;
  const usable = Math.max(closing, MAX_SPEED_RUN * 0.35);
  return d / usable + turnCost;
}

/**
 * Pressure on a player, as a time-weighted threat rather than a distance.
 *
 * Only opponents he can SEE contribute (§10.2 — acting on information he does
 * not have is cheating, and the same rule already governs the AI's intercept
 * and pass decisions).
 */
export function calculatePressure(
  state: WorldState, id: number, out: PressureInfo,
): PressureInfo {
  const p = state.players;
  const team = p.team[id]!;
  const px = p.px[id]!;
  const pz = p.pz[id]!;
  out.value = 0;
  out.primaryOpponent = -1;
  out.directionX = 0;
  out.directionZ = 0;
  out.arrivalTime = Infinity;

  let sumX = 0;
  let sumZ = 0;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (p.active[i] !== 1 || p.sentOff[i] === 1 || p.team[i]! === team) continue;
    const d = distXZ(px, pz, p.px[i]!, p.pz[i]!);
    if (d > PRESSURE_MAX_RANGE_M) continue;
    if (!perceives(state, id, p.px[i]!, p.pz[i]!)) continue;
    const t = estimatePlayerArrival(state, i, px, pz);
    // A threat that lands inside the horizon is full pressure; one that lands
    // at the horizon is none.
    const contribution = clamp01(1 - t / PRESSURE_HORIZON_S);
    if (contribution <= 0) continue;
    // Direction is FROM him TOWARD the player: where the pressure is pushing.
    const ux = (px - p.px[i]!) / (d || 1);
    const uz = (pz - p.pz[i]!) / (d || 1);
    sumX += ux * contribution;
    sumZ += uz * contribution;
    if (t < out.arrivalTime) {
      out.arrivalTime = t;
      out.primaryOpponent = i;
    }
    // Threats combine, but the second man adds less than the first — being
    // closed down by two is worse than by one and not twice as bad.
    out.value = out.value + contribution * (1 - out.value);
  }
  const len = Math.sqrt(sumX * sumX + sumZ * sumZ);
  if (len > 1e-4) {
    out.directionX = sumX / len;
    out.directionZ = sumZ / len;
  }
  return out;
}

/**
 * How exposed is the carrier's ball, 0..1? (Phase-1 Commit 6 — BAD TOUCH)
 *
 * The audit found that a heavy touch already made a tackle more likely to
 * SUCCEED (`tackleSuccessChance`'s proximity term) but never made a defender
 * more likely to TRY. That is backwards: a bad touch is the single most
 * readable invitation in football, and a defender who does not react to one
 * looks asleep.
 *
 * Two terms, because a ball is exposed either by being far from the carrier's
 * feet or by moving away from him faster than he can follow:
 *   • distance beyond TOUCH_RADIUS (the sim's own "under control" radius)
 *   • speed relative to the carrier
 *
 * Returns 0 for a ball glued to the laces, 1 for one that has genuinely got
 * away. `carrier` may be -1 (nobody has it) — a loose ball is fully exposed.
 */
export function ballExposure(state: WorldState, carrier: number): number {
  if (carrier < 0) return 1;
  const p = state.players;
  const b = state.ball;
  const d = distXZ(p.px[carrier]!, p.pz[carrier]!, b.px, b.pz);
  const rel = Math.sqrt(
    (b.vx - p.vx[carrier]!) * (b.vx - p.vx[carrier]!) +
    (b.vz - p.vz[carrier]!) * (b.vz - p.vz[carrier]!),
  );
  const byDistance = clamp01((d - TOUCH_RADIUS) / (BALL_EXPOSED_DIST_M - TOUCH_RADIUS));
  const bySpeed = clamp01(rel / BALL_EXPOSED_REL_SPEED);
  // Either alone is enough to expose it, so combine as "not both under
  // control" rather than as an average.
  return 1 - (1 - byDistance) * (1 - bySpeed);
}

/** True once the ball is loose enough to be worth leaving a contain for. */
export function isBallExposed(state: WorldState, carrier: number): boolean {
  return ballExposure(state, carrier) > 0.5;
}

/**
 * Should `defender` challenge `carrier` right now? 0..1.
 *
 * ⚠ CALL THIS ONLY AFTER `reachesBallBeforeMan` HAS PASSED. That predicate is
 * a hard gate and this is a preference; collapsing the two into one number is
 * how a defender reasons his way into a foul because everything else about the
 * moment looked good.
 *
 * Credits:
 *   + the ball is exposed — the reason to go
 *   + he gets there first, by a margin, in time
 *   + there is cover behind him — a challenge you can afford to lose
 * Debits:
 *   − he is the last man, where losing it is a goal
 *   − a marginal ball-first margin, which is a foul waiting to be given
 */
export function scoreTackleOpportunity(
  state: WorldState, defender: number, carrier: number,
): number {
  const p = state.players;
  const exposure = ballExposure(state, carrier);

  // Arrival advantage: he must beat the carrier to the ball, and by enough
  // that the carrier's next touch cannot simply move it first.
  const tDef = estimatePlayerArrival(state, defender, state.ball.px, state.ball.pz);
  const tAtk = estimatePlayerArrival(state, carrier, state.ball.px, state.ball.pz);
  const advantage = clamp01((tAtk - tDef) / PRESSURE_HORIZON_S + 0.5);

  // Cover behind him: a team-mate goal-side of this duel means a lost
  // challenge is a foul-free inconvenience rather than a chance conceded.
  const team = p.team[defender]!;
  let support = 0;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (i === defender || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! !== team) continue;
    if (distXZ(p.px[i]!, p.pz[i]!, p.px[defender]!, p.pz[defender]!) <= DUEL_SUPPORT_RADIUS_M) {
      support = 1;
      break;
    }
  }

  let score = exposure * DUEL_OPPORTUNITY_W_EXPOSURE
    + advantage * DUEL_OPPORTUNITY_W_ADVANTAGE
    + support * DUEL_OPPORTUNITY_W_SUPPORT;

  // A margin barely over the line is a challenge the referee decides, not the
  // defender. Scale the whole thing down as it approaches zero.
  const margin = ballFirstMargin(state, defender, carrier);
  score *= clamp01(margin / POSSESSION_RADIUS + 0.5);

  if (isLastDefender(state, defender)) score *= 1 - DUEL_LAST_DEFENDER_PENALTY;
  return clamp01(score);
}

/** Is he the last man between the ball and his own goal? */
export function isLastDefender(state: WorldState, id: number): boolean {
  const p = state.players;
  const team = p.team[id]!;
  // Own goal is at -attackSign * halfLength; team 0 attacks +x when
  // teamAAttacksPositiveX, mirroring ball-out.ts.
  const attacksPositiveX = state.teamAAttacksPositiveX ? team === 0 : team === 1;
  const ownGoalSign = attacksPositiveX ? -1 : 1;
  const mine = p.px[id]! * ownGoalSign;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (i === id || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! !== team) continue;
    if (p.gkHoldStartTick[i]! >= 0) continue;      // the keeper is not cover
    if (state.positions[i] === 'GK') continue;
    if (p.px[i]! * ownGoalSign > mine) return false; // somebody is deeper
  }
  return true;
}

/**
 * SHOULD HE LEAVE CONTAIN? (the ENGAGE/CHALLENGE split)
 *
 * ⚠ THIS DELIBERATELY DOES NOT MULTIPLY BY `ballFirstMargin`, and that is the
 * whole point of the split.
 *
 * `scoreTackleOpportunity` does, and it made ENGAGE structurally unreachable:
 * CONTAIN puts the defender GOAL-SIDE, which by construction means
 * defender → attacker's body → ball, so the margin is negative and the product
 * collapsed. MEASURED over a 90 s AI match: p50 = 0.000, p90 = 0.020, and
 * ENGAGE was entered ZERO times. The positioning behaviour we want from
 * CONTAIN was destroying the score required to leave CONTAIN.
 *
 * The two questions are not one question:
 *   ENGAGE    "an opportunity is developing and I am committing movement to it"
 *   CHALLENGE "the ball is now actually reachable, legally and physically"
 * Ball-first geometry belongs to the second. A defender is SUPPOSED to still be
 * goal-side when he starts his step; the step is what earns him the geometry.
 *
 * `trend` is the strongest term and the one that makes this read as football:
 * exposure jumping from 0.18 to 0.62 is a heavy touch, and a heavy touch is
 * when a real defender goes. Level alone would have him lunging at a ball that
 * is merely a long way in front of a carrier who is in complete control of it.
 */
export function scoreEngageOpportunity(
  state: WorldState, defender: number, carrier: number,
  exposure: number, trend: number,
): number {
  const p = state.players;
  const b = state.ball;

  const tDef = estimatePlayerArrival(state, defender, b.px, b.pz);
  const tAtk = estimatePlayerArrival(state, carrier, b.px, b.pz);
  const advantage = clamp01((tAtk - tDef) / PRESSURE_HORIZON_S + 0.5);

  const team = p.team[defender]!;
  let support = 0;
  for (let i = 0; i < MAX_PLAYERS; i++) {
    if (i === defender || p.active[i] !== 1 || p.sentOff[i] === 1) continue;
    if (p.team[i]! !== team) continue;
    if (distXZ(p.px[i]!, p.pz[i]!, p.px[defender]!, p.pz[defender]!) <= DUEL_SUPPORT_RADIUS_M) {
      support = 1;
      break;
    }
  }

  // A rising exposure is worth more than a high flat one: the first is a
  // mistake happening now, the second is just a long carry.
  const rising = clamp01(trend / ENGAGE_TREND_FULL);

  let score = exposure * ENGAGE_W_EXPOSURE
    + rising * ENGAGE_W_TREND
    + advantage * ENGAGE_W_ADVANTAGE
    + support * ENGAGE_W_SUPPORT;

  // Too far away to make the step meaningful.
  const d = distXZ(p.px[defender]!, p.pz[defender]!, b.px, b.pz);
  score *= 1 - clamp01((d - ENGAGE_STEP_RANGE_M) / ENGAGE_STEP_RANGE_M);

  if (isLastDefender(state, defender)) score *= 1 - DUEL_LAST_DEFENDER_PENALTY;
  return clamp01(score);
}

/**
 * Where should an ENGAGING defender step? Not at the carrier's centre — at the
 * ball, led by how long he needs to get there, so the step is what turns a
 * negative ballFirstMargin positive.
 */
export function engageStepPoint(
  state: WorldState, defender: number, out: { x: number; z: number },
): void {
  const b = state.ball;
  const t = Math.min(estimatePlayerArrival(state, defender, b.px, b.pz), ENGAGE_LEAD_MAX_S);
  out.x = b.px + b.vx * t;
  out.z = b.pz + b.vz * t;
}
